import { type ActionEvent, Controller } from "@hotwired/stimulus"

import DrawerController from "@/controllers/drawer_controller"
import BookingFooterController from "@/controllers/venue/booking_footer_controller"
import RoomCounterController from "@/controllers/venue/room_counter_controller"
import { type StimulusDispatchOptions } from "@/entry_points/types"
import { formatEEEDMMMYYYY } from "@/helpers/date_format"
import * as Ga4 from "@/helpers/ga4_helper"
import { loadBackgroundImages } from "@/helpers/image_loader"
import { pluralize } from "@/helpers/pluralize"
import { ENTRY, prefetchEntry } from "@/helpers/prefetch"

export default class extends Controller {
  static targets = [
    "calendarEditButton",
    "dateRadio",
    "dateCheck",
    "dateSummary",
    "formSubmitButton",
    "hiddenDateInput",
    "maxPaxNotice",
    "modalHeader",
    "paxCheck",
    "paxInput",
    "paxSummary",
    "roomsStream",
    "unavailableDateNotice"
  ]
  declare readonly calendarEditButtonTarget: HTMLButtonElement
  declare readonly hasCalendarEditButtonTarget: boolean
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly dateCheckTarget: HTMLElement
  declare readonly dateSummaryTarget: HTMLElement
  declare readonly formSubmitButtonTarget: HTMLButtonElement
  declare readonly hiddenDateInputTarget: HTMLInputElement
  declare readonly maxPaxNoticeTarget: HTMLElement
  declare readonly modalHeaderTarget: HTMLElement
  declare readonly paxCheckTarget: HTMLElement
  declare readonly paxInputTarget: HTMLInputElement
  declare readonly paxSummaryTarget: HTMLElement
  declare readonly unavailableDateNoticeTarget: HTMLElement

  static outlets = ["booking-footer", "drawer", "room-counter"]
  declare readonly bookingFooterOutlet: BookingFooterController
  declare readonly drawerOutlets: DrawerController[]
  declare readonly roomCounterOutlets: RoomCounterController[]

  static values = { ecommerceItem: Object, nights: Number }
  declare readonly ecommerceItemValue: Ga4.EcommerceItem
  declare readonly nightsValue: number

  calendarStreamTargetConnected(): void {
    const isDateSelected = !!this.selectedIsoDate

    prefetchEntry(ENTRY.BOOKINGS)

    // Hide notice when date is selected. We don't show it in the opposite case,
    // otherwise notice would show on load whenever there's no date in params, which
    // we don't want (if no date was selected, it can't be "unavailable").
    if (isDateSelected) {
      this.unavailableDateNoticeTarget.hidden = true
    }

    this.setCalendarSubmit(isDateSelected)
  }

  facilitiesStreamTargetConnected(): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(false)
  }

  roomsStreamTargetConnected(element: HTMLElement): void {
    // Load fallback image for rooms with no carousel
    loadBackgroundImages(element.querySelectorAll("[data-img]"))

    this.bookingFooterOutlet.setRoomsSubmit(false)
  }

  addToCart(): void {
    Ga4.addToCartEvent(
      {
        ...this.ecommerceItemValue,
        item_category3: this.selectedPax.toString(),
        price: this.priceForSelectedRooms,
        quantity: 1
      },
      this.priceForSelectedRooms
    )
  }

  confirmDate(): void {
    const formattedDate = formatEEEDMMMYYYY(new Date(this.selectedIsoDate!))
    const formattedNights = this.nightsValue > 0 ? `(${pluralize(this.nightsValue, "night")})` : ""

    this.dateSummaryTarget.textContent = `${formattedDate} ${formattedNights}`
    this.dateCheckTarget.hidden = false

    // Update the hidden date target in the pax drawer, so it will overwrite any date from params.
    // That input is necessary to enable skipping the calendar drawer if date already present.
    this.hiddenDateInputTarget.value = this.selectedIsoDate!
  }

  confirmPax({ params: { price } }: ActionEvent): void {
    this.paxSummaryTarget.textContent = pluralize(this.selectedPax, "guest")
    this.paxCheckTarget.hidden = false

    // Confirming pax goes to calendar step and price summary updated there, except:
    // 1. If page loaded with date param there's no calendar and thus no "selected" date. In that
    //    case we get the price from server as a param to update price summary.
    if (price) {
      this.bookingFooterOutlet.setPriceTarget(+price * this.selectedPax)
    }

    // 2. If date is already selected (e.g. going back to pax step from another step). In that
    //    case re-click saved date to update price summary for latest pax value.
    const savedDate = this.dateRadioTargets.find(
      (radio) => radio.value === this.hiddenDateInputTarget.value
    )
    savedDate?.click()
  }

  dateSelected({ params: { price }, target }: ActionEvent): void {
    this.unavailableDateNoticeTarget.hidden = true

    if (price && (target as HTMLInputElement).checked) {
      this.bookingFooterOutlet.setPriceTarget(+price * this.selectedPax)
      this.setCalendarSubmit(true)
    } else {
      this.setCalendarSubmit(false)
    }
  }

  facilitySelected(): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(true)
  }

  onCalendarDrawerClick(e: ActionEvent): void {
    if (this.hasCalendarEditButtonTarget) {
      e.stopPropagation()
      this.calendarEditButtonTarget.click()
    } else {
      this.calendarStreamTargetConnected()
    }
  }

  onPaxDrawerOpen(): void {
    this.bookingFooterOutlet.setPaxSubmit()
  }

  roomSelected(countChangedOptions: StimulusDispatchOptions): void {
    const remainingPax = this.selectedPax - this.paxForSelectedRooms
    const { delta, name } = countChangedOptions.detail!

    this.roomCounterOutlets.forEach((counter) => {
      // Counters with the same `name` represent one room, with different occupancies. The available
      // units for a room are for all occupancies, so when one changes we increase/decrease
      // availability of all of them. This value is used in max calculation, so must be set before
      // max below.
      if (counter.name === name) {
        counter.setAvailable(delta as number)
      }

      counter.setMax(remainingPax)
    })

    this.bookingFooterOutlet.setPriceTarget(this.priceForSelectedRooms)
    this.bookingFooterOutlet.setRoomsSubmit(remainingPax === 0)
  }

  scrollDrawerIntoView(e: TransitionEvent): void {
    // Note: We run this function on `transitionend` event instead of `drawer:open` because the
    // transition starts immediately when the drawer is opened and it interferes with
    // `scrollIntoView`.

    // There are several transitions firing on child elements in the drawer, but we only want to
    // run this function once, when the `grid-template-rows` transition finishes (since that's the
    // one that affects `scrollIntoView`).
    // Ideally we'd simply debounce these events instead of doing this check, but because there are
    // multiple drawers that are interdependent and thus fire events simultaneously, we can't be
    // certain which will be the last event.
    if (e.propertyName !== "grid-template-rows") {
      return
    }

    const drawer = this.drawerOutlets.find((drawer) =>
      drawer.element.contains(e.target as HTMLElement)
    )!

    if (drawer.openValue) {
      drawer.element.style.setProperty(
        "--drawer-top-offset",
        `${this.modalHeaderTarget.offsetHeight}px`
      )
      drawer.element.scrollIntoView({ behavior: "smooth" })
    }
  }

  showCalendarEditButton(): void {
    this.calendarEditButtonTarget.hidden = false
  }

  submitForm(): void {
    this.formSubmitButtonTarget.click()
  }

  toggleMaxPaxNotice({ detail }: StimulusDispatchOptions): void {
    const { count, max } = detail!
    this.maxPaxNoticeTarget.hidden = (count as number) < (max as number)
  }

  private get selectedIsoDate(): string | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)?.value
  }

  private get selectedPax(): number {
    return +this.paxInputTarget.value
  }

  private setCalendarSubmit(isDateSelected: boolean): void {
    this.bookingFooterOutlet.setCalendarSubmit({
      isDateSelected,
      isSpaDay: this.nightsValue === 0
    })
  }

  private get paxForSelectedRooms(): number {
    return this.roomCounterOutlets.reduce((sum, counter) => sum + counter.selectedPax, 0)
  }

  private get priceForSelectedRooms(): number {
    return this.roomCounterOutlets.reduce((sum, counter) => sum + counter.selectedPrice, 0)
  }
}
